import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, AlertCircle, CheckCircle, Info, Sprout } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BackToHome from './BackToHome';
import {
  validateInput,
  validatePassword,
  validateEmail,
  isCommonWeakPassword,
  INPUT_LIMITS,
  getCharacterCount,
  sanitizeInputRealtime
} from '../lib/input-sanitization';
import {
  recordFailedLogin,
  isLoginBlocked,
  clearLoginAttempts,
  getLoginIdentifier
} from '../lib/rate-limiting';
import { logger } from '../lib/logger';

export default function AuthForm() {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<ReturnType<typeof validatePassword> | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const { signIn, signUp } = useAuth();

  // Validate password in real-time for signup
  useEffect(() => {
    if (isSignUp && password) {
      const validation = validatePassword(password);
      setPasswordValidation(validation);
      setShowPasswordRequirements(true);
    } else {
      setPasswordValidation(null);
      setShowPasswordRequirements(false);
    }
  }, [password, isSignUp]);

  const handleFullNameChange = (value: string) => {
    // BUG-H001 FIX: Sanitize input as it's typed (realtime - no trim)
    const sanitized = sanitizeInputRealtime(value);
    // BUG-M002 FIX: Enforce max length
    if (sanitized.length <= INPUT_LIMITS.FULL_NAME) {
      setFullName(sanitized);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setWarning('');

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setError(emailValidation.error || 'Please enter a valid email');
      return;
    }

    if (isSignUp) {
      // SIGNUP FLOW

      // BUG-H001 & BUG-M002 FIX: Validate and sanitize full name
      const nameValidation = validateInput(fullName, {
        maxLength: INPUT_LIMITS.FULL_NAME,
        fieldName: 'Full name',
        required: true
      });

      if (!nameValidation.valid) {
        setError(nameValidation.error || 'Please enter your full name');
        return;
      }

      // BUG-M001 FIX: Password validation
      if (!passwordValidation || !passwordValidation.valid) {
        setError('Please meet all password requirements');
        return;
      }

      // Check for common weak passwords
      if (isCommonWeakPassword(password)) {
        setError('This password is too common. Please choose a more unique password.');
        return;
      }

      setLoading(true);

      try {
        // Use sanitized name
        const { error: signUpError } = await signUp(
          email.trim().toLowerCase(),
          password,
          nameValidation.sanitized
        );

        if (signUpError) {
          // Handle duplicate email specifically
          if (signUpError.message.toLowerCase().includes('already') ||
              signUpError.message.toLowerCase().includes('registered')) {
            setError('This email is already registered. Please sign in instead.');
            setWarning('Already have an account?');
            setTimeout(() => setIsSignUp(false), 3000);
          } else {
            logger.error('Signup error', signUpError);
            setError(signUpError.message || 'Unable to create account. Please try again.');
          }
        } else {
          // Account created successfully
          // FEATURE FLAG: Email verification disabled - users can access immediately
          setSuccess(
            'Account created successfully! Redirecting to complete your profile...'
          );
          logger.action('user_signup', { email });
          // AuthContext will handle navigation automatically
        }
      } catch (err) {
        logger.error('Signup exception', err);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // LOGIN FLOW

      // BUG-H002 FIX: Check rate limiting
      const identifier = getLoginIdentifier(email);
      const rateLimitCheck = isLoginBlocked(identifier);

      if (rateLimitCheck.blocked) {
        setError(rateLimitCheck.message || 'Too many login attempts. Please try again later.');
        return;
      }

      // Show warning if approaching limit
      if (rateLimitCheck.remainingAttempts && rateLimitCheck.remainingAttempts <= 2) {
        setWarning(rateLimitCheck.message || '');
      }

      setLoading(true);

      try {
        const { error: signInError } = await signIn(email.trim().toLowerCase(), password);

        if (signInError) {
          // BUG-H002 FIX: Record failed attempt
          recordFailedLogin(identifier);

          // Better error messages
          if (signInError.message.toLowerCase().includes('invalid')) {
            const check = isLoginBlocked(identifier);
            if (check.remainingAttempts && check.remainingAttempts > 0) {
              setError(
                `Invalid email or password. ${check.remainingAttempts} attempt${check.remainingAttempts !== 1 ? 's' : ''} remaining.`
              );
            } else {
              setError('Invalid email or password.');
            }
          // FEATURE FLAG: Email verification disabled
          // Uncomment the following block to re-enable email verification requirement
          /*
          } else if (signInError.message.toLowerCase().includes('not confirmed') ||
                     signInError.message.toLowerCase().includes('email not verified')) {
            setError(
              'Please verify your email address before signing in. Check your inbox for the verification link.'
            );
          */
          } else {
            logger.error('Login error', signInError);
            setError(signInError.message || 'Unable to sign in. Please try again.');
          }
        } else {
          // BUG-H002 FIX: Clear attempts on successful login
          clearLoginAttempts(identifier);
          logger.action('user_login', { email });
          // Navigation handled by AuthContext
        }
      } catch (err) {
        recordFailedLogin(identifier);
        logger.error('Login exception', err);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const nameCharCount = getCharacterCount(fullName, INPUT_LIMITS.FULL_NAME);

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8">
        <div className="mb-6">
          <BackToHome to="/" />
        </div>

        <div className="text-center mb-8">
          <div className="text-[#8CAFA8] flex justify-center mb-4">
            <Sprout className="w-12 h-12" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-semibold text-[#1F2937] mb-2">
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </h2>
          <p className="text-[#6B7280]">
            {isSignUp
              ? 'Start receiving AI-powered feedback today'
              : 'Sign in to access your feedback dashboard'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => handleFullNameChange(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-[12px] focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                  placeholder="John Doe"
                  required
                  maxLength={INPUT_LIMITS.FULL_NAME}
                />
              </div>
              {/* BUG-M002 FIX: Character counter */}
              <div className="mt-1 text-xs text-right">
                <span className={nameCharCount.warning ? 'text-orange-600' : 'text-gray-500'}>
                  {nameCharCount.count}/{INPUT_LIMITS.FULL_NAME}
                </span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-[12px] focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="you@example.com"
                maxLength={INPUT_LIMITS.EMAIL}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-[12px] focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="••••••••"
              />
            </div>

            {/* BUG-M001 FIX: Password strength indicator */}
            {showPasswordRequirements && passwordValidation && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`h-1 flex-1 rounded-full ${
                    passwordValidation.strength === 'strong' ? 'bg-green-500' :
                    passwordValidation.strength === 'medium' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`} />
                  <span className={`text-xs font-medium ${
                    passwordValidation.strength === 'strong' ? 'text-green-600' :
                    passwordValidation.strength === 'medium' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {passwordValidation.strength === 'strong' ? 'Strong' :
                     passwordValidation.strength === 'medium' ? 'Medium' :
                     'Weak'}
                  </span>
                </div>

                <div className="space-y-1">
                  {[
                    { check: password.length >= 8, label: 'At least 8 characters' },
                    { check: /[A-Z]/.test(password), label: 'One uppercase letter' },
                    { check: /[a-z]/.test(password), label: 'One lowercase letter' },
                    { check: /[0-9]/.test(password), label: 'One number' },
                    { check: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~;']/.test(password), label: 'One special character' },
                  ].map((requirement, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      {requirement.check ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-200" />
                      )}
                      <span className={`text-xs ${requirement.check ? 'text-green-600' : 'text-gray-500'}`}>
                        {requirement.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {warning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">{warning}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-[12px] p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-[12px] p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (isSignUp && passwordValidation && !passwordValidation.valid)}
            className="w-full bg-[#8CAFA8] text-white py-3 rounded-[12px] font-medium hover:bg-[#7A9B94] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            {loading && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create account' : 'Sign in')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
              setSuccess('');
              setWarning('');
              setPassword('');
            }}
            className="text-sm text-[#8CAFA8] hover:text-[#7A9B94] font-medium"
          >
            {isSignUp
              ? 'Already have an account? Sign in'
              : "Don't have an account? Sign up"}
          </button>
        </div>
      </div>
    </div>
  );
}
